/**
 * The one way the front end talks to the API.
 *
 * Since ADR-010 the student is identified by Clerk's session token, sent as a
 * bearer token on every call. The token lives a minute and Clerk refreshes it,
 * so it is asked for per request, never stored here. No cookies go to the API
 * at all: credentials are omitted, which also means a page on another site can
 * never make the browser call the API as the student.
 *
 * Every reply is parsed against the shared contract before anything uses it. A
 * body that does not match is treated as the API being unavailable, not as
 * data, because a screen built on a half understood reply is worse than a
 * screen that says it could not reach the server.
 */

import { apiBase } from "./config";
import { loadClerk } from "./auth/clerk";
import { readHint } from "./auth/hint";

/** What a shared schema offers: a structural type, so this file needs nothing from zod itself. */
export interface Contract<T> {
  safeParse(value: unknown): { success: true; data: T } | { success: false };
}

export interface ApiReply {
  readonly status: number;
  /** The parsed JSON body, or null when there was none or it was not JSON. */
  readonly body: unknown;
}

/** The narrow surface the session logic needs, so tests can hand it a fake. */
export interface Transport {
  /** A token of its own is for the first request of a page load, made before Clerk has loaded. */
  get(path: string, options?: { readonly token?: string }): Promise<ApiReply>;
  post(path: string, body?: unknown): Promise<ApiReply>;
}

/**
 * Where each request's bearer token comes from.
 *
 * Until Clerk has loaded, the session token Clerk keeps in a cookie, if it has
 * life left, so a page can call the API in the same moments Clerk's script is
 * starting up; failing that, Clerk itself, waited for. App.tsx swaps in Clerk's
 * own once it is loaded.
 */
let tokenSource: () => Promise<string | null> = async () =>
  readHint().token ?? (await (await loadClerk()).session?.getToken()) ?? null;

export function setTokenSource(source: () => Promise<string | null>): void {
  tokenSource = source;
}

/** The API could not be reached, or answered with something outside the contract. */
export class ApiUnavailable extends Error {
  override readonly name = "ApiUnavailable";
}

async function send(method: "GET" | "POST" | "PATCH" | "PUT", path: string, body?: unknown, given?: string): Promise<ApiReply> {
  if (apiBase === null) {
    throw new ApiUnavailable("No API base is configured for this build. Set VITE_API_BASE and rebuild.");
  }
  let res: Response;
  try {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const token = given ?? (await tokenSource());
    if (token) headers["Authorization"] = `Bearer ${token}`;
    res = await fetch(`${apiBase}/api${path}`, {
      method,
      credentials: "omit",
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    throw new ApiUnavailable(error instanceof Error ? error.message : "The request did not complete.");
  }
  const parsed: unknown = await res.json().catch(() => null);
  return { status: res.status, body: parsed };
}

export const http: Transport & { patch(path: string, body: unknown): Promise<ApiReply>; put(path: string, body: unknown): Promise<ApiReply> } = {
  get: (path, options) => send("GET", path, undefined, options?.token),
  post: (path, body) => send("POST", path, body ?? {}),
  patch: (path, body) => send("PATCH", path, body),
  /** A setting replaced whole: the admin site's AI spend level (day 37). */
  put: (path, body) => send("PUT", path, body),
};

/**
 * A reply the student saves rather than reads, such as their data export.
 *
 * Fetched with the bearer token like any call, then handed to the browser as a
 * download from memory, since a plain link cannot carry the token. Null when
 * the API refused, with the status, so the caller can say why.
 */
export async function download(path: string): Promise<{ status: number; blob: Blob | null; filename: string | null }> {
  if (apiBase === null) {
    throw new ApiUnavailable("No API base is configured for this build. Set VITE_API_BASE and rebuild.");
  }
  let res: Response;
  try {
    const token = await tokenSource();
    res = await fetch(`${apiBase}/api${path}`, {
      credentials: "omit",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (error) {
    throw new ApiUnavailable(error instanceof Error ? error.message : "The request did not complete.");
  }
  if (!res.ok) return { status: res.status, blob: null, filename: null };
  const named = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  return { status: res.status, blob: await res.blob(), filename: named?.[1] ?? null };
}

/**
 * A file sent to a signed upload URL (day 13's PUT), with the bearer token,
 * the file's own type and nothing else. The URL is absolute: the API gave it.
 */
export async function putFile(url: string, file: Blob, type: string): Promise<ApiReply> {
  let res: Response;
  try {
    const token = await tokenSource();
    res = await fetch(url, {
      method: "PUT",
      credentials: "omit",
      headers: { "Content-Type": type, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
  } catch (error) {
    throw new ApiUnavailable(error instanceof Error ? error.message : "The request did not complete.");
  }
  return { status: res.status, body: await res.json().catch(() => null) };
}

/** A DELETE, with the bearer token. */
export async function remove(path: string): Promise<ApiReply> {
  if (apiBase === null) {
    throw new ApiUnavailable("No API base is configured for this build. Set VITE_API_BASE and rebuild.");
  }
  let res: Response;
  try {
    const token = await tokenSource();
    res = await fetch(`${apiBase}/api${path}`, {
      method: "DELETE",
      credentials: "omit",
      headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  } catch (error) {
    throw new ApiUnavailable(error instanceof Error ? error.message : "The request did not complete.");
  }
  return { status: res.status, body: await res.json().catch(() => null) };
}

/** Saves a blob the way a download link would, without navigating anywhere. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  // After the browser has taken the file, not before.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/**
 * The body as the contract says it is, or ApiUnavailable.
 *
 * For replies where only one shape is acceptable. Callers that branch on an
 * error code read `reply.body` with the error schema themselves.
 */
export function expect<T>(reply: ApiReply, schema: Contract<T>, path: string): T {
  const parsed = schema.safeParse(reply.body);
  if (!parsed.success) {
    throw new ApiUnavailable(`${path} answered ${reply.status} with a body outside the contract.`);
  }
  return parsed.data;
}
